import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const PolicyEditorPanel = withListOverflow(PolicyListOwner);
// src/features/admin/PolicyEditorPanel.jsx
//
// ADMIN-POLICY-EDITOR: Space Policy admin panel.
//
// Three sections:
//   1. Baseline — company-wide knobs (Utilization Assumptions + Guardrails)
//   2. Per-office overrides — table of offices with active overrides
//   3. Bulk-apply — apply knobs to offices filtered by region or market
//
// Role gate: admins can edit; viewers/editors see read-only state.
//
// All colours via T tokens; no raw hex. No new npm packages.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SharingRatioValue } from "../../components/SharingRatioValue.jsx";
import { T } from "../../app/tokens.js";
import { DefinitionPolicyCards } from "./DefinitionPolicyCards.jsx";
import { refreshSpacePolicy } from "../../services/spacePolicyStore.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";
import { AuthError, ValidationError } from "../../data/errors.js";
import {
  getPolicy,
  putPolicyBaseline,
  putPolicyOverride,
  deletePolicyOverride,
  bulkApplyPolicy,
} from "../../data/policyClient.js";

// ─── Style constants ──────────────────────────────────────────────────────────

const TITLE = {
  margin: "0 0 6px 0",
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  letterSpacing: -0.2,
};

const SUBTITLE = {
  fontSize: T.fsBase,
  color: T.mid,
  marginBottom: 20,
  lineHeight: 1.5,
};

const SECTION_DIVIDER = {
  border: "none",
  borderTop: `1px solid ${T.border}`,
  margin: "28px 0",
};

const GROUP_TITLE = {
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  color: T.mid,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  marginBottom: 12,
};

const KNOB_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: "14px 20px",
  marginBottom: 18,
};

const KNOB_FIELD = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const KNOB_LABEL = {
  fontSize: T.fsXs,
  fontWeight: T.fwSemibold,
  color: T.mid,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const KNOB_INPUT = {
  padding: "5px 8px",
  fontSize: T.fsBase,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  fontFamily: "inherit",
  color: T.dark,
  background: T.white,
  width: "100%",
  boxSizing: "border-box",
};

const KNOB_INPUT_ERROR = {
  ...KNOB_INPUT,
  borderColor: T.red,
};

const INLINE_ERROR = {
  fontSize: T.fsXs,
  color: T.red,
  marginTop: 2,
};

const TABLE_WRAPPER = {
  border: `0.5px solid ${T.border}`,
  borderRadius: 6,
  overflowX: "auto",
  marginBottom: 16,
};

const TH = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: T.fsXxs,
  fontWeight: T.fwSemibold,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: T.mid,
  background: T.lightGray,
  borderBottom: `0.5px solid ${T.border}`,
};

const TD = {
  padding: "10px 14px",
  fontSize: T.fsBase,
  color: T.dark,
  borderBottom: `0.5px solid ${T.border}`,
  verticalAlign: "middle",
};

const BTN_PRIMARY = {
  background: T.blue,
  color: T.white,
  border: "none",
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwBold,
  padding: "6px 16px",
  cursor: "pointer",
};

const BTN_SECONDARY = {
  background: T.white,
  color: T.blue,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  padding: "6px 14px",
  cursor: "pointer",
};

const BTN_DANGER = {
  background: T.white,
  color: T.red,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  padding: "6px 14px",
  cursor: "pointer",
};

const BTN_DISABLED = {
  opacity: 0.5,
  cursor: "not-allowed",
};

const READ_ONLY_BANNER = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 6,
  background: T.lightGray,
  border: `0.5px solid ${T.border}`,
  fontSize: T.fsBase,
  color: T.mid,
  marginBottom: 18,
};

const COLLAPSIBLE_HEADER = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  padding: "10px 0",
  userSelect: "none",
};

const SECTION_HEADER = {
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  margin: "0 0 6px 0",
};

// ─── Knob definitions ─────────────────────────────────────────────────────────

const UTIL_ASSUMPTIONS = [
  { key: "days_in_office_per_mo", label: "Days in office / month", unit: "days",  step: 1,   min: 1,   max: 31  },
  { key: "desk_sharing_ratio",    label: "Desk sharing ratio",      unit: "ratio", step: 0.1, min: 1.0, max: 2.0,
    fmt: (v) => (v != null && v !== "") ? <SharingRatioValue numerator={Number(v).toFixed(1)} /> : "—" },
  { key: "hoteling_hold_pct",     label: "Hoteling hold",           unit: "%",     step: 1,   min: 0,   max: 100 },
  { key: "confroom_giveback_pct", label: "Conf-room giveback",      unit: "%",     step: 1,   min: 0,   max: 100 },
];

const GUARDRAILS = [
  { key: "teamblock_waste_pct", label: "Teamblock waste", unit: "%",      step: 1, min: 0, max: 100 },
  { key: "density_floor_sf",    label: "Density floor",   unit: "sf/seat", step: 1, min: 1           },
  { key: "target_buffer_pct",   label: "Target buffer",   unit: "%",      step: 1, min: 0, max: 100  },
];

const ALL_KNOBS = [...UTIL_ASSUMPTIONS, ...GUARDRAILS];
const COMPANY_GUARDRAILS = GUARDRAILS.filter(k => k.key !== "density_floor_sf");
const BASELINE_KNOBS = [...UTIL_ASSUMPTIONS, ...COMPANY_GUARDRAILS];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function fmtKnob(row, key) {
  const v = row?.[key];
  if (v == null) return "—";
  if (key === "desk_sharing_ratio") return <SharingRatioValue numerator={Number(v).toFixed(1)} />;
  return String(Number(v));
}

function emptyDraft(baseline, fields = ALL_KNOBS) {
  const out = { effective_from: today() };
  for (const { key } of fields) {
    out[key] = baseline?.[key] != null ? String(Number(baseline[key])) : "";
  }
  return out;
}

// ─── KnobGroup ────────────────────────────────────────────────────────────────

function KnobGroup({ title, knobs, draft, errors, onChange, disabled }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={GROUP_TITLE}>{title}</div>
      <div style={KNOB_GRID}>
        {knobs.map(({ key, label, unit, step, min, max, fmt }) => (
          <div key={key} style={KNOB_FIELD}>
            <label
              htmlFor={`knob-${key}`}
              style={KNOB_LABEL}
            >
              {label}{unit ? ` (${unit})` : ""}
            </label>
            {disabled && fmt ? (
              <span
                id={`knob-${key}`}
                data-testid={`knob-${key}`}
                style={{ ...KNOB_INPUT, display: "block" }}
              >
                {fmt(draft[key])}
              </span>
            ) : (
              <input
                id={`knob-${key}`}
                data-testid={`knob-${key}`}
                type="number"
                step={step ?? "any"}
                min={min != null ? min : undefined}
                max={max != null ? max : undefined}
                disabled={disabled}
                value={draft[key] ?? ""}
                onChange={(e) => onChange(key, e.target.value)}
                style={errors?.[key] ? KNOB_INPUT_ERROR : KNOB_INPUT}
              />
            )}
            {errors?.[key] && (
              <div style={INLINE_ERROR}>{errors[key]}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BaselineSection ──────────────────────────────────────────────────────────

function BaselineSection({ baseline, revision, isAdmin, onSaved, onError }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editRevision, setEditRevision] = useState(null);
  const [original, setOriginal] = useState({});
  function startEdit() {
    const initial = emptyDraft(baseline, BASELINE_KNOBS);
    setDraft(initial); setOriginal(initial); setEditRevision(revision);
    setErrors({});
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
    setErrors({});
  }

  function setField(key, val) {
    setDraft((d) => ({ ...d, [key]: val }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  }

  async function onSave() {
    if (saving) return;
    setSaving(true);
    setErrors({});
    const knobs = {};
    for (const { key } of BASELINE_KNOBS) {
      if (draft[key] !== original[key] && draft[key] !== "" && draft[key] != null) knobs[key] = Number(draft[key]);
    }
    try {
      await putPolicyBaseline({ knobs, effective_from: draft.effective_from, baseline_revision: editRevision });
      setEditing(false);
      setDraft(null);
      showToast("Baseline saved.", { type: "success" });
      onSaved();
      refreshSpacePolicy().catch(onError);
    } catch (err) {
      if (err instanceof ValidationError && err.details?.fields) {
        setErrors(err.details.fields);
      } else if (err instanceof ValidationError) {
        setErrors({ _form: err.message });
      } else if (err instanceof AuthError) {
        onError(new Error("Permission denied. Only admins can update the policy baseline."));
      } else {
        log.error("PolicyEditorPanel: putPolicyBaseline failed", { err: err?.message });
        onError(new Error("Save failed. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  }

  const displayDraft = editing ? draft : emptyDraft(baseline, BASELINE_KNOBS);

  return (
    <div data-testid="policy-baseline-section">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <h2 style={SECTION_HEADER}>Company baseline</h2>
        {isAdmin && !editing && (
          <button
            type="button"
            data-testid="baseline-edit-btn"
            onClick={startEdit}
            style={BTN_SECONDARY}
          >
            Edit
          </button>
        )}
      </div>
      <p style={{ ...SUBTITLE, marginBottom: 16 }}>
        Company-wide defaults applied to all offices that don't have a specific override.
        {baseline?.effectiveFrom && !editing && (
          <span style={{ marginLeft: 8, color: T.mid }}>
            Effective {fmtDate(baseline.effectiveFrom)}.
          </span>
        )}
      </p>

      {errors._form && <div style={{ ...INLINE_ERROR, marginBottom: 10 }}>{errors._form}</div>}

      <KnobGroup
        title="Utilization Assumptions"
        knobs={UTIL_ASSUMPTIONS}
        draft={displayDraft}
        errors={editing ? errors : {}}
        onChange={setField}
        disabled={!isAdmin || !editing}
      />
      <KnobGroup
        title="Guardrails"
        knobs={COMPANY_GUARDRAILS}
        draft={displayDraft}
        errors={editing ? errors : {}}
        onChange={setField}
        disabled={!isAdmin || !editing}
      />

      {editing && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
          <div style={KNOB_FIELD}>
            <label htmlFor="baseline-effective-from" style={KNOB_LABEL}>Effective from</label>
            <input
              id="baseline-effective-from"
              data-testid="baseline-effective-from"
              type="date"
              value={draft.effective_from}
              onChange={(e) => setDraft((d) => ({ ...d, effective_from: e.target.value }))}
              style={{ ...KNOB_INPUT, width: 160 }}
            />
          </div>
          <div style={{ marginTop: 20 }}>
            <button
              type="button"
              data-testid="baseline-save-btn"
              onClick={onSave}
              disabled={saving}
              style={{ ...BTN_PRIMARY, ...(saving ? BTN_DISABLED : {}) }}
            >
              {saving ? "Saving…" : "Save baseline"}
            </button>
            <button
              type="button"
              data-testid="baseline-cancel-btn"
              onClick={cancelEdit}
              style={{ ...BTN_SECONDARY, marginLeft: 8 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OverrideRow inline editor ─────────────────────────────────────────────────

function OverrideForm({ locations, baseline, initial, onSave, onCancel, saving }) {
  const [draft, setDraft] = useState(() => {
    if (initial) {
      const d = { effective_from: initial.effectiveFrom || today() };
      for (const { key } of ALL_KNOBS) {
        d[key] = initial[key] != null ? String(Number(initial[key])) : (baseline?.[key] != null ? String(Number(baseline[key])) : "");
      }
      return d;
    }
    return emptyDraft(baseline);
  });
  const [locationId, setLocationId] = useState(initial?.rippleLocationId ?? "");
  const [errors, setErrors] = useState({});

  function setField(key, val) {
    setDraft((d) => ({ ...d, [key]: val }));
    if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  }

  function handleSave() {
    if (!locationId && !initial) {
      setErrors({ _loc: "Select an office." });
      return;
    }
    const knobs = {};
    for (const { key } of ALL_KNOBS) {
      if (draft[key] !== "" && draft[key] != null) knobs[key] = Number(draft[key]);
    }
    onSave(initial ? initial.rippleLocationId : locationId, {
      knobs,
      effective_from: draft.effective_from,
    });
  }

  return (
    <div
      data-testid="override-form"
      style={{ padding: "14px 16px", background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 6, marginBottom: 10 }}
    >
      {!initial && (
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="override-location-select" style={KNOB_LABEL}>Office</label>
          <select
            id="override-location-select"
            data-testid="override-location-select"
            value={locationId}
            onChange={(e) => { setLocationId(e.target.value); if (errors._loc) setErrors((e) => { const n = { ...e }; delete n._loc; return n; }); }}
            style={{ ...KNOB_INPUT, marginTop: 4, width: "auto", minWidth: 240 }}
          >
            <option value="">Select an office…</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.name} ({loc.id})</option>
            ))}
          </select>
          {errors._loc && <div style={INLINE_ERROR}>{errors._loc}</div>}
        </div>
      )}

      <KnobGroup
        title="Utilization Assumptions"
        knobs={UTIL_ASSUMPTIONS}
        draft={draft}
        errors={errors}
        onChange={setField}
        disabled={false}
      />
      <KnobGroup
        title="Guardrails"
        knobs={GUARDRAILS}
        draft={draft}
        errors={errors}
        onChange={setField}
        disabled={false}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
        <div style={KNOB_FIELD}>
          <label htmlFor="override-effective-from" style={KNOB_LABEL}>Effective from</label>
          <input
            id="override-effective-from"
            data-testid="override-effective-from"
            type="date"
            value={draft.effective_from}
            onChange={(e) => setDraft((d) => ({ ...d, effective_from: e.target.value }))}
            style={{ ...KNOB_INPUT, width: 160 }}
          />
        </div>
        <div style={{ marginTop: 20 }}>
          <button
            type="button"
            data-testid="override-save-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ ...BTN_PRIMARY, ...(saving ? BTN_DISABLED : {}) }}
          >
            {saving ? "Saving…" : "Save override"}
          </button>
          <button
            type="button"
            data-testid="override-cancel-btn"
            onClick={onCancel}
            style={{ ...BTN_SECONDARY, marginLeft: 8 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OverridesSection ─────────────────────────────────────────────────────────

function OverridesSection({ overrides, locations, baseline, isAdmin, onSaved, onError }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [clearingId, setClearingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const locationMap = useMemo(() => {
    const m = {};
    for (const loc of (locations ?? [])) m[loc.id] = loc;
    return m;
  }, [locations]);

  async function handleSaveOverride(locationId, payload) {
    setSaving(true);
    try {
      await putPolicyOverride(locationId, payload);
      setAdding(false);
      setEditingId(null);
      onSaved();
    } catch (err) {
      if (err instanceof AuthError) {
        onError(new Error("Permission denied."));
      } else {
        log.error("PolicyEditorPanel: putPolicyOverride failed", { err: err?.message });
        onError(new Error("Save failed. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleClear(locationId) {
    if (!window.confirm(`Clear the policy override for ${locationMap[locationId]?.name ?? locationId}? This reverts it to the company baseline.`)) return;
    setClearingId(locationId);
    try {
      await deletePolicyOverride(locationId);
      onSaved();
    } catch (err) {
      if (err instanceof AuthError) {
        onError(new Error("Permission denied."));
      } else {
        log.error("PolicyEditorPanel: deletePolicyOverride failed", { err: err?.message });
        onError(new Error("Clear failed. Please try again."));
      }
    } finally {
      setClearingId(null);
    }
  }

  // Deduplicate: show the most-recent row per location.
  const deduped = useMemo(() => {
    const seen = new Set();
    return (overrides ?? []).filter((o) => {
      if (seen.has(o.rippleLocationId)) return false;
      seen.add(o.rippleLocationId);
      return true;
    });
  }, [overrides]);

  // Locations not yet overridden — for the add picker.
  const availableLocations = useMemo(() => {
    const overriddenIds = new Set(deduped.map((o) => o.rippleLocationId));
    return (locations ?? []).filter((l) => !overriddenIds.has(l.id));
  }, [locations, deduped]);

  return (
    <div data-testid="policy-overrides-section">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
        <h2 style={SECTION_HEADER}>Per-office overrides</h2>
        {isAdmin && !adding && (
          <button
            type="button"
            data-testid="add-override-btn"
            onClick={() => { setAdding(true); setEditingId(null); }}
            style={BTN_SECONDARY}
          >
            + Add override
          </button>
        )}
      </div>
      <p style={{ ...SUBTITLE, marginBottom: 16 }}>
        Offices listed here use their own knob values instead of the company baseline.
      </p>

      {adding && (
        <OverrideForm
          locations={availableLocations}
          baseline={baseline}
          initial={null}
          onSave={handleSaveOverride}
          onCancel={() => setAdding(false)}
          saving={saving}
        />
      )}

      {deduped.length === 0 && !adding ? (
        <div
          data-testid="overrides-empty"
          style={{ padding: "32px 24px", textAlign: "center", color: T.light,
                   fontSize: T.fsBase, background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 6 }}
        >
          No per-office overrides yet. {isAdmin ? 'Use \u201cAdd override\u201d to set one.' : ""}
        </div>
      ) : (
        <div style={TABLE_WRAPPER}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={TH}>Office</th>
                {ALL_KNOBS.map(({ key, label }) => (
                  <th key={key} style={TH}>{label}</th>
                ))}
                <th style={TH}>Effective</th>
                {isAdmin && <th style={TH}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {deduped.map((row) => (
                <React.Fragment key={row.rippleLocationId}>
                  <tr>
                    <td style={TD}>
                      <div style={{ fontWeight: T.fwSemibold }}>{locationMap[row.rippleLocationId]?.name ?? row.rippleLocationId}</div>
                      <div style={{ fontSize: T.fsXs, color: T.light }}>{row.rippleLocationId}</div>
                    </td>
                    {ALL_KNOBS.map(({ key }) => (
                      <td key={key} style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>{fmtKnob(row, key)}</td>
                    ))}
                    <td style={TD}>{fmtDate(row.effectiveFrom)}</td>
                    {isAdmin && (
                      <td style={TD}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            data-testid={`edit-override-btn-${row.rippleLocationId}`}
                            onClick={() => { setEditingId(row.rippleLocationId); setAdding(false); }}
                            style={BTN_SECONDARY}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            data-testid={`clear-override-btn-${row.rippleLocationId}`}
                            onClick={() => handleClear(row.rippleLocationId)}
                            disabled={clearingId === row.rippleLocationId}
                            style={{ ...BTN_DANGER, ...(clearingId === row.rippleLocationId ? BTN_DISABLED : {}) }}
                          >
                            {clearingId === row.rippleLocationId ? "Clearing…" : "Clear"}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {editingId === row.rippleLocationId && (
                    <tr>
                      <td colSpan={ALL_KNOBS.length + (isAdmin ? 3 : 2)} style={{ padding: 0 }}>
                        <OverrideForm
                          locations={locations}
                          baseline={baseline}
                          initial={row}
                          onSave={handleSaveOverride}
                          onCancel={() => setEditingId(null)}
                          saving={saving}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── BulkApplySection ────────────────────────────────────────────────────────

function BulkApplySection({ locations, regions, markets, isAdmin, onSaved, onError }) {
  const [open, setOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [knobEnabled, setKnobEnabled] = useState(() => {
    const m = {};
    for (const { key } of ALL_KNOBS) m[key] = false;
    return m;
  });
  const [knobValues, setKnobValues] = useState(() => {
    const m = {};
    for (const { key } of ALL_KNOBS) m[key] = "";
    return m;
  });
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null); // { locationIds[] }
  const [errors, setErrors] = useState({});

  // Compute preview locationIds based on region/market filters.
  const filteredLocationIds = useMemo(() => {
    return (locations ?? [])
      .filter((loc) => {
        if (selectedRegions.length > 0 && !selectedRegions.includes(loc.region)) return false;
        if (selectedMarkets.length > 0 && !selectedMarkets.includes(loc.marketId)) return false;
        return true;
      })
      .map((l) => l.id);
  }, [locations, selectedRegions, selectedMarkets]);

  function toggleRegion(region) {
    setSelectedRegions((prev) =>
      prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]
    );
    setPreview(null);
  }

  function toggleMarket(market) {
    setSelectedMarkets((prev) =>
      prev.includes(market) ? prev.filter((m) => m !== market) : [...prev, market]
    );
    setPreview(null);
  }

  function previewCount() {
    setPreview({ locationIds: filteredLocationIds });
  }

  async function handleApply() {
    if (applying) return;
    const targetIds = preview?.locationIds ?? filteredLocationIds;
    if (targetIds.length === 0) {
      setErrors({ _form: "No offices match the selected filters." });
      return;
    }
    const knobs = {};
    for (const { key } of ALL_KNOBS) {
      if (knobEnabled[key] && knobValues[key] !== "") knobs[key] = Number(knobValues[key]);
    }
    if (Object.keys(knobs).length === 0) {
      setErrors({ _form: "Select at least one knob to apply." });
      return;
    }
    setErrors({});
    setApplying(true);
    try {
      const result = await bulkApplyPolicy({ locationIds: targetIds, knobs, effective_from: effectiveFrom });
      showToast(`Applied to ${result.applied} office${result.applied === 1 ? "" : "s"}.`, { type: "success" });
      setPreview(null);
      onSaved();
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors({ _form: err.message });
      } else if (err instanceof AuthError) {
        onError(new Error("Permission denied."));
      } else {
        log.error("PolicyEditorPanel: bulkApplyPolicy failed", { err: err?.message });
        onError(new Error("Bulk apply failed. Please try again."));
      }
    } finally {
      setApplying(false);
    }
  }

  if (!isAdmin) return null;

  return (
    <div data-testid="policy-bulk-section">
      <div
        data-testid="bulk-apply-toggle"
        style={COLLAPSIBLE_HEADER}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen((o) => !o); }}
        aria-expanded={open}
      >
        <h2 style={{ ...SECTION_HEADER, margin: 0 }}>Bulk apply</h2>
        <span style={{ color: T.mid, fontSize: T.fsBase }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ paddingTop: 4 }}>
          <p style={{ ...SUBTITLE, marginBottom: 16 }}>
            Apply the same knob values to multiple offices at once.
            Region and market are filters only - they are not stored on the policy row.
          </p>

          {errors._form && <div style={{ ...INLINE_ERROR, marginBottom: 10 }}>{errors._form}</div>}

          {/* Region filter */}
          {(regions ?? []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={GROUP_TITLE}>Filter by region</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {regions.map((r) => (
                  <label
                    key={r}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5,
                             fontSize: T.fsBase, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRegions.includes(r)}
                      onChange={() => toggleRegion(r)}
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Market filter */}
          {(markets ?? []).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={GROUP_TITLE}>Filter by market</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {markets.map((m) => (
                  <label
                    key={m}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5,
                             fontSize: T.fsBase, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedMarkets.includes(m)}
                      onChange={() => toggleMarket(m)}
                    />
                    {m}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Knob selection */}
          <div style={{ marginBottom: 16 }}>
            <div style={GROUP_TITLE}>Knobs to apply</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "10px 20px" }}>
              {ALL_KNOBS.map(({ key, label, unit, step, min, max }) => (
                <label
                  key={key}
                  style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsBase, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    data-testid={`bulk-knob-check-${key}`}
                    checked={knobEnabled[key]}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setKnobEnabled((prev) => ({ ...prev, [key]: checked }));
                      if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
                    }}
                  />
                  <span style={{ minWidth: 160 }}>{label}{unit ? ` (${unit})` : ""}</span>
                  <input
                    data-testid={`bulk-knob-input-${key}`}
                    type="number"
                    step={step ?? "any"}
                    min={min != null ? min : undefined}
                    max={max != null ? max : undefined}
                    disabled={!knobEnabled[key]}
                    value={knobValues[key]}
                    onChange={(e) => setKnobValues((v) => ({ ...v, [key]: e.target.value }))}
                    style={{ ...KNOB_INPUT, width: 90, opacity: knobEnabled[key] ? 1 : 0.4 }}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Effective from */}
          <div style={{ ...KNOB_FIELD, maxWidth: 200, marginBottom: 16 }}>
            <label htmlFor="bulk-effective-from" style={KNOB_LABEL}>Effective from</label>
            <input
              id="bulk-effective-from"
              data-testid="bulk-effective-from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              style={KNOB_INPUT}
            />
          </div>

          {/* Preview + Apply */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              data-testid="bulk-preview-btn"
              onClick={previewCount}
              style={BTN_SECONDARY}
            >
              Preview count
            </button>

            {preview && (
              <span data-testid="bulk-preview-count" style={{ fontSize: T.fsBase, color: T.dark }}>
                {preview.locationIds.length} office{preview.locationIds.length === 1 ? "" : "s"} matched
              </span>
            )}

            <button
              type="button"
              data-testid="bulk-apply-btn"
              onClick={handleApply}
              disabled={applying}
              style={{ ...BTN_PRIMARY, ...(applying ? BTN_DISABLED : {}) }}
            >
              {applying ? "Applying…" : `Apply to ${preview ? preview.locationIds.length : filteredLocationIds.length} office${(preview ? preview.locationIds.length : filteredLocationIds.length) === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PolicyEditorPanel ────────────────────────────────────────────────────────

function PolicyListOwner({ currentRole, explainer } = {}) {
  const isAdmin = currentRole === "admin";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloads, setReloads] = useState(0);

  const [baseline, setBaseline] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [locations, setLocations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [revision, setRevision] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (reloads === 0) setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getPolicy();
        if (cancelled) return;
        setBaseline(data.baseline ?? null);
        setRevision(data.revision);
        setOverrides(data.overrides ?? []);
        setLocations(data.locations ?? []);
        setRegions(data.regions ?? []);
        setMarkets(data.markets ?? []);
      } catch (err) {
        if (cancelled) return;
        log.error("PolicyEditorPanel: getPolicy failed", { err: err?.message });
        if (reloads === 0) setError(err); else setPanelError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloads]);

  const onSaved = useCallback(() => setReloads((n) => n + 1), []);

  const [panelError, setPanelError] = useState(null);
  const onError = useCallback((err) => setPanelError(err), []);

  if (loading) {
    return (
      <div data-testid="policy-editor-panel">
        <h1 style={TITLE}>Space Policy</h1>
        {explainer}
        <div data-testid="policy-editor-loading" style={{ color: T.mid, padding: "40px 0", textAlign: "center" }}>
          Loading policy…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="policy-editor-panel">
        <h1 style={TITLE}>Space Policy</h1>
        {explainer}
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
      </div>
    );
  }

  return (
    <div data-testid="policy-editor-panel">
      <h1 style={TITLE}>Space Policy</h1>
      <DefinitionPolicyCards baseline={baseline} isAdmin={isAdmin} onSaved={onSaved} />

      {!isAdmin && (
        <div data-testid="policy-read-only-banner" style={READ_ONLY_BANNER}>
          <span>🔒</span>
          <span>Read-only - only admins can edit policy knobs.</span>
        </div>
      )}

      {panelError && (
        <ErrorBanner error={panelError} onDismiss={() => setPanelError(null)} />
      )}

      <BaselineSection
        baseline={baseline}
        revision={revision}
        isAdmin={isAdmin}
        onSaved={onSaved}
        onError={onError}
      />

      <hr style={SECTION_DIVIDER} />

      <OverridesSection
        overrides={overrides}
        locations={locations}
        baseline={baseline}
        isAdmin={isAdmin}
        onSaved={onSaved}
        onError={onError}
      />

      {isAdmin && (
        <>
          <hr style={SECTION_DIVIDER} />
          <BulkApplySection
            locations={locations}
            regions={regions}
            markets={markets}
            isAdmin={isAdmin}
            onSaved={onSaved}
            onError={onError}
          />
        </>
      )}
    </div>
  );
}
